import { Box, IconButton, Theme, alpha, styled, useTheme } from "@mui/material";
import React, { createContext, useContext, useState } from "react";
import { IoClose } from "react-icons/io5";
import { TextOverflow } from "../TextOverflow";

type TooltipData = {
    body: React.ReactNode;
    mobileHeader: string;
};

type TooltipProviderState = {
    setTooltip: (data: TooltipData | null) => Promise<void>;
}

const initialState: TooltipProviderState = {
    setTooltip: async () => { },
};

export type TooltipProviderProps = {
    children: React.ReactNode;
};

const TooltipProviderContext = createContext<TooltipProviderState>(initialState);

export const TooltipProvider = ({ children }: TooltipProviderProps) => {
    const theme: Theme = useTheme();

    const [tooltipVisible, setTooltipVisible] = useState(false);

    const [tooltip, setTooltipState] = useState<TooltipData | null>(null);

    const setTooltip = async (data: TooltipData | null) => {
        if (data) {
            setTooltipVisible(true);
            setTooltipState(data);
            return;
        }

        setTooltipVisible(false);

        const duration = theme.transitions.duration.enteringScreen || 250;

        await new Promise((resolve) => setTimeout(resolve, duration));

        setTooltipState(null);
    }

    return (
        <TooltipProviderContext.Provider value={{ setTooltip }}>
            {children}

            <>
                <StyledMobileTooltipOverlay onClick={() => setTooltip(null)} open={!!tooltipVisible} />

                <StyledMobileTooltipWrapper open={!!tooltipVisible}>
                    {tooltip && (
                        <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
                            <StyleMobileTooltipHeader>
                                <Box sx={{ width: "calc(100% - 40px)" }}>
                                    <TextOverflow width="100%" variant="h6" text={tooltip.mobileHeader} />
                                </Box>

                                <IconButton onClick={() => setTooltip(null)} sx={{ flexShrink: 0 }}>
                                    <IoClose />
                                </IconButton>
                            </StyleMobileTooltipHeader>

                            <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1, overflowX: "hiden", overflowY: "auto", paddingX: 2, paddingBottom: 4 }}>
                                {tooltip.body}
                            </Box>
                        </Box>
                    )}
                </StyledMobileTooltipWrapper>
            </>
        </TooltipProviderContext.Provider>
    );
}

TooltipProvider.useTooltip = () => {
    const context = useContext(TooltipProviderContext);

    if (!context) {
        throw new Error("useTooltip must be used within a TooltipProvider");
    }

    return context;
};

export const StyledMobileTooltipWrapper = styled("div", {})<{ open: boolean }>(({ theme, open }) => ({
    position: "fixed",
    bottom: 0,
    left: 0,
    width: "100vw",
    zIndex: theme.zIndex.tooltip,
    borderTopLeftRadius: `${theme.shape.borderRadius * 2.5}px`,
    borderTopRightRadius: `${theme.shape.borderRadius * 2.5}px`,
    backgroundColor: theme.palette.background.default,
    maxHeight: "80vh",
    overflowX: "hidden",
    minHeight: "25vh",
    pointerEvents: open ? "auto" : "none",
    transform: open ? "translateY(0)" : "translateY(100%)",
    transition: theme.transitions.create(["transform"], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
}));

export const StyledMobileTooltipOverlay = styled("div", {})<{ open: boolean }>(({ theme, open }) => ({
    position: "fixed",
    bottom: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    zIndex: theme.zIndex.tooltip - 1,
    backgroundColor: alpha(theme.palette.common.black, 0.5),
    opacity: open ? 1 : 0,
    visibility: open ? "visible" : "hidden",
    pointerEvents: open ? "auto" : "none",
    transition: theme.transitions.create(["opacity"], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
}));

export const StyleMobileTooltipHeader = styled("div", {})(({ theme }) => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2),
    paddingRight: theme.spacing(1),
    width: "100%",
}));